<template>
  <div>
    <v-container fluid>
      <v-row>
        <!-- Navigation bar on the left -->
        <v-col cols="12" sm="12" md="3" lg="2">
          <div class="nav-container">
            <!-- Back to problems list -->
            <v-btn
              color="primary"
              @click="goToHome"
              class="back-button"
              prepend-icon="mdi-arrow-left"
            >
              All Problems
            </v-btn>

            <!-- Tab navigation -->
            <div class="tab-navigation">
              <v-btn
                :class="{ selected: activeTab === 'problem' }"
                @click="activeTab = 'problem'"
                class="tab-button"
              >
                Problem
              </v-btn>
              <v-btn
                :class="{ selected: activeTab === 'solution' }"
                @click="activeTab = 'solution'"
                class="tab-button"
              >
                Solution
              </v-btn>
            </div>

            <!-- Section navigation based on active tab -->
            <div class="section-navigation">
              <NavigationBar :mode="activeTab" />
            </div>
          </div>
        </v-col>

        <!-- Main content -->
        <v-col cols="12" sm="12" md="9" lg="8" class="problem-content">
          <div v-if="loading" class="loading-container">
            <v-progress-circular indeterminate color="primary" />
            <p>Loading problem...</p>
          </div>

          <div v-else-if="error" class="error-container">
            <p>{{ error }}</p>
            <v-btn color="primary" @click="goToHome">Back to Problems</v-btn>
          </div>

          <template v-else>
            <!-- Problem Header -->
            <div class="problem-header">
              <h1 class="problem-title">
                Problem #{{ problemId }} -
                {{
                  activeTab === "problem" ? "Problem Definition" : "Solution"
                }}
              </h1>
              <v-divider class="my-3" color="primary" />
              <p v-if="problemInfo?.context_name" class="problem-context">
                <v-icon size="small">mdi-map-marker</v-icon>
                {{ problemInfo.context_name }}
              </p>
            </div>

            <!-- Problem Tab Content -->
            <div v-show="activeTab === 'problem'">
              <ProblemDetails
                :problemId="problemId"
                :livingLab="livingLab"
                :labName="labName"
                :isNewProblem="isNewProblem"
                @labSelected="onLabSelected"
              />
            </div>

            <!-- Solution Tab Content -->
            <div v-show="activeTab === 'solution'">
              <SolutionDetails :problemId="problemId" :livingLab="livingLab" />
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import apiClient from "@/plugins/apiService";
import "@mdi/font/css/materialdesignicons.css";

import NavigationBar from "@/components/NavigationBar.vue";
import ProblemDetails from "@/components/ProblemDetails.vue";
import SolutionDetails from "@/components/SolutionDetails.vue";

export default defineComponent({
  name: "ProblemPage",
  components: {
    NavigationBar,
    ProblemDetails,
    SolutionDetails,
  },
  data() {
    return {
      activeTab: "problem",
      problemId: null,
      livingLab: null,
      labName: null,
      problemInfo: null,
      loading: true,
      error: null,
      isNewProblem: false,
    };
  },
  created() {
    this.problemId = this.$route.params.id;
    this.isNewProblem = this.$route.params.isNewProblem === "true";

    // Clear the state after reading it
    if (this.$route.state?.isNewProblem) {
      this.$router.replace({
        name: "ProblemPage",
        params: { id: this.problemId },
      });
    }
    this.fetchProblemData();
  },
  watch: {
    "$route.params.id"(newId) {
      this.problemId = newId;
      this.fetchProblemData();
    },
    activeTab() {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 10);
    },
  },
  methods: {
    async fetchProblemData() {
      if (!this.problemId) {
        this.error = "No problem ID provided";
        this.loading = false;
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        const response = await apiClient.post(`/problem_data`, {
          problemId: this.problemId,
        });
        this.problemInfo = response.data;
        this.livingLab = this.problemInfo.context_id;
        // Fetch lab name if available
        if (this.livingLab) {
          const labResponse = await apiClient.get(`/labs`, {
            params: { id: this.livingLab },
          });
          const labData = labResponse.data;
          if (labData.length > 0) {
            this.labName = labData[0].name;
          }
        }
      } catch (error) {
        console.error("Error fetching problem data:", error);
        this.error = "Failed to load problem. It may not exist.";
      } finally {
        this.loading = false;
      }
    },
    onLabSelected(selectedLabData) {
      if (selectedLabData && typeof selectedLabData === "object") {
        this.livingLab = selectedLabData.id;
        this.labName = selectedLabData.name;
      } else {
        this.livingLab = selectedLabData;
      }
    },
    goToHome() {
      this.$router.push({ name: "HomePage" });
    },
  },
});
</script>

<style scoped>
.nav-container {
  position: sticky;
  top: 0;
  z-index: 1000;
  padding-top: 20px;
}

.back-button {
  width: 100%;
  margin-bottom: 20px;
}

.tab-navigation {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.tab-button {
  flex: 1;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 20px;
}

.error-container {
  color: var(--contrastColor);
}

.problem-content {
  background-color: var(--backgroundColorLight);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin-top: 30px;
}

.problem-header {
  padding-bottom: 20px;
}

.problem-description {
  white-space: pre-line;
}
</style>
